import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import artworksData from '../data/artworks.json';

const ArtworkContainer = styled.div`
  padding: 2rem;
  margin-top: 90px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const BackButton = styled.button`
  padding: 0.5rem 1rem;
  margin-bottom: 2rem;
  background: none;
  border: 1px solid #333;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #333;
    color: white;
  }
`;

const ArtworkImage = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: 2rem;
`;

const ArtworkInfo = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
`;

const Year = styled.div`
  color: #666;
  margin-bottom: 1rem;
`;

const Medium = styled.div`
  color: #666;
  margin-bottom: 3.125rem;
`;

const Description = styled.div`
  white-space: pre-line;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const AdditionalInfo = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
  
  strong {
    display: inline-block;
    min-width: 150px;
    margin-right: 1rem;
    font-weight: 400;
  }
`;

const AvailabilitySection = styled.div`
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const AvailabilityHeading = styled.h3`
  margin-bottom: 1rem;
  color: #333;
`;

const AvailabilityText = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 0.5rem;
  
  a {
    text-decoration: underline;
  }
`;

const ShopButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  background: #000;
  color: #fff;
  text-decoration: none;
  border-radius: 999px;
  margin: 0.75rem 0 1rem;
  font-weight: 500;

  &:hover {
    opacity: 0.85;
  }
`;

const InformationHeading = styled(AvailabilityHeading)``;

const StatusItem = styled(InfoItem)`
  margin-top: 10px;
`;

const ArtworkDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);

  useEffect(() => {
    const foundArtwork = artworksData.find(art => art.slug === slug);
    setArtwork(foundArtwork);
    
    // Set the page title when artwork is found
    if (foundArtwork) {
      document.title = `${foundArtwork.title} - SLEEPR`;
    } else {
      document.title = 'Artwork Not Found - SLEEPR';
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'SLEEPR';
    };
  }, [slug]);

  if (!artwork) {
    return <div>Artwork not found</div>;
  }

  const numberAvailable = artwork.number_available;
  const status = artwork.availability;
  const medium = artwork.medium;
  const paperType = artwork.print;

  return (
    <ArtworkContainer>
      <ArtworkImage src={`/img${artwork.image_large}.jpg`} alt={artwork.title} />
      
      <ArtworkInfo>
        <Title>{artwork.title}</Title>
        
        <Description>{artwork.description}</Description>
        
        <AdditionalInfo>
          <InformationHeading>Information</InformationHeading>
          <InfoItem>
            <strong>Year:</strong>
            {artwork.year}
          </InfoItem>
          <InfoItem>
            <strong>Size:</strong>
            {artwork.size}
          </InfoItem>
          <InfoItem>
            <strong>Number available:</strong>
            {numberAvailable}
          </InfoItem>
          <InfoItem>
            <strong>Medium:</strong>
            {medium}
          </InfoItem>
          <InfoItem>
            <strong>Paper type:</strong>
            {paperType}
          </InfoItem>
          <InfoItem>
            <strong>Series:</strong>
            {artwork.series}
          </InfoItem>
          <StatusItem>
            <strong>Status:</strong>
            {status}
          </StatusItem>
        </AdditionalInfo>
        
        <AvailabilitySection>
          <AvailabilityHeading>Availability</AvailabilityHeading>
          <AvailabilityText>
            If you are interested in purchasing this specific work above, please the studio manager at{' '}
            <a href="mailto:reception@sleeprstudio.com">reception@sleeprstudio.com</a> who can help provide detailed
            information about current availability, pricing, and acquisition options.
          </AvailabilityText>
          <AvailabilityText>
            Limited edition prints are sometimes made available on the{' '}
            <a href="https://shop.sleeprstudio.com" target="_blank" rel="noreferrer">
              Sleepr Studio Official Store
            </a>
            .
          </AvailabilityText>

          <ShopButton href="https://shop.sleeprstudio.com" target="_blank" rel="noreferrer">
            Visit the Sleepr Studio Store
          </ShopButton>
        </AvailabilitySection>
      </ArtworkInfo>
    </ArtworkContainer>
  );
};

export default ArtworkDetail; 